import { useCallback, useEffect, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { apiClient } from '@/api/client';

interface RecaptchaConfig {
  enabled: boolean;
  siteKey: string;
  minScore: number;
}

interface RecaptchaVerifyResponse {
  success: boolean;
  score: number | null;
  action: string | null;
  errors: string[];
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const DEFAULT_CONFIG: RecaptchaConfig = {
  enabled: false,
  siteKey: '',
  minScore: 0.5,
};

const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-v3';

let configCache: RecaptchaConfig | null = null;
let configPromise: Promise<RecaptchaConfig> | null = null;
let scriptPromise: Promise<void> | null = null;

async function loadRecaptchaConfig(): Promise<RecaptchaConfig> {
  if (configCache) return configCache;
  if (!configPromise) {
    configPromise = apiClient
      .get<RecaptchaConfig>('/security/recaptcha/config')
      .then((response) => {
        configCache = {
          enabled: Boolean(response.data?.enabled),
          siteKey: String(response.data?.siteKey ?? ''),
          minScore: Number(response.data?.minScore ?? DEFAULT_CONFIG.minScore),
        };
        return configCache;
      })
      .catch(() => DEFAULT_CONFIG)
      .finally(() => {
        configPromise = null;
      });
  }

  return configPromise;
}

function ensureRecaptchaScript(siteKey: string): Promise<void> {
  if (window.grecaptcha) {
    return Promise.resolve();
  }

  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script.')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'));
      document.head.appendChild(script);
    }).catch((error) => {
      scriptPromise = null;
      throw error;
    });
  }

  return scriptPromise;
}

function extractRecaptchaError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error && (error as any).response?.data?.detail) {
    const detail = (error as any).response.data.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String(detail.message);
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function useRecaptcha() {
  const [config, setConfig] = useState<RecaptchaConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const ensureConfig = useCallback(async (): Promise<RecaptchaConfig> => {
    const nextConfig = await loadRecaptchaConfig();
    setConfig((current) => {
      if (
        current.enabled === nextConfig.enabled &&
        current.siteKey === nextConfig.siteKey &&
        current.minScore === nextConfig.minScore
      ) {
        return current;
      }

      return nextConfig;
    });

    if (!nextConfig.enabled || !nextConfig.siteKey) {
      setLoading(false);
      return nextConfig;
    }

    try {
      await ensureRecaptchaScript(nextConfig.siteKey);
      return nextConfig;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const setup = async () => {
      const nextConfig = await loadRecaptchaConfig();
      if (!active) return;

      setConfig(nextConfig);

      if (!nextConfig.enabled || !nextConfig.siteKey) {
        setLoading(false);
        return;
      }

      try {
        await ensureRecaptchaScript(nextConfig.siteKey);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void setup();

    return () => {
      active = false;
    };
  }, []);

  const execute = useCallback(
    async (action: string): Promise<string | null> => {
      const currentConfig = await ensureConfig();

      if (!currentConfig.enabled) return null;
      if (!currentConfig.siteKey) {
        throw new Error('reCAPTCHA site key is not configured.');
      }

      await ensureRecaptchaScript(currentConfig.siteKey);

      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA is not available in the browser.');
      }

      return new Promise<string>((resolve) => {
        const timer = setTimeout(() => {
          console.warn('reCAPTCHA execution timed out after 5s');
          resolve('');
        }, 5000);

        window.grecaptcha?.ready(() => {
          window.grecaptcha
            ?.execute(currentConfig.siteKey, { action })
            .then((token) => {
              clearTimeout(timer);
              resolve(token);
            })
            .catch((err) => {
              clearTimeout(timer);
              console.warn('Could not obtain a reCAPTCHA token:', err);
              resolve('');
            });
        });
      });
    },
    [ensureConfig],
  );

  const verifyAction = useCallback(
    async (action: string): Promise<RecaptchaVerifyResponse> => {
      const token = await execute(action);
      if (!token) {
        return { success: true, score: null, action, errors: [] };
      }

      try {
        const response = await apiClient.post<RecaptchaVerifyResponse>('/security/recaptcha/verify', {
          token,
          action,
        });

        if (!response.data.success) {
          throw new Error('reCAPTCHA validation failed.');
        }

        return response.data;
      } catch (error) {
        throw new Error(extractRecaptchaError(error, 'Could not verify reCAPTCHA. Please try again.'));
      }
    },
    [execute],
  );

  const getRequestConfig = useCallback(
    async (action: string): Promise<AxiosRequestConfig> => {
      const token = await execute(action);
      if (!token) return {};
      return {
        headers: {
          'X-Recaptcha-Token': token,
        },
      };
    },
    [execute],
  );

  return {
    enabled: config.enabled,
    minScore: config.minScore,
    loading,
    verifyAction,
    getRequestConfig,
    getErrorMessage: extractRecaptchaError,
  };
}

export {};